/**
 * managerReviewDraftService — load and save review drafts via the proxy.
 *
 * Drafts are stored in a private Airtable base (DRAFTS_BASE_ID).
 * Each draft is keyed by manager email + employee ID.
 * Status: 'Draft' while in progress, 'Submitted' after Mark Complete.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

export interface DraftRecord {
  recordId: string;
  /** Only present on records returned from GET /drafts (the list endpoint). */
  employeeId?: string | null;
  employeeName?: string | null;
  draftJson: string;
  status: 'Draft' | 'Submitted';
  lastSaved: string | null;
}

export async function loadDraft(employeeId: string): Promise<DraftRecord | null> {
  try {
    const res = await authenticatedFetch(
      `${PROXY_URL}/api/people/manager-review/draft/${encodeURIComponent(employeeId)}`,
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.draft ?? null;
  } catch {
    return null;
  }
}

/**
 * Fetches all submitted reviews for the current manager from Airtable.
 * Used on mount to restore completedReviews across hard refreshes and devices.
 * Returns [] on error so callers never need to handle failures.
 */
export async function loadAllSubmittedDrafts(): Promise<DraftRecord[]> {
  try {
    const res = await authenticatedFetch(
      `${PROXY_URL}/api/people/manager-review/drafts`,
    );
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.drafts) ? data.drafts : [];
  } catch {
    return [];
  }
}

export async function saveDraft(payload: {
  employeeId: string;
  employeeName: string;
  draftJson: string;
  status?: 'Draft' | 'Submitted';
}): Promise<{ recordId: string; status: string; lastSaved: string } | null> {
  try {
    const res = await authenticatedFetch(
      `${PROXY_URL}/api/people/manager-review/draft`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}
